import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", dob: "", phone: "", email: "" };

export const CheckoutModal = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const [orderId, setOrderId] = useState("");

    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener("open-checkout", handler);
        return () => window.removeEventListener("open-checkout", handler);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (status === "sending") return;
        setStatus("sending");
        setError("");
        try {
            const res = await fetch(`${API_BASE}/api/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
            setOrderId(data.order_id || "");
            setStatus("qr");
        } catch (err) {
            setError(err.message);
            setStatus("error");
        }
    };

    const claim = async () => {
        if (status === "claiming") return;
        setStatus("claiming");
        setError("");
        try {
            const res = await fetch(`${API_BASE}/api/orders/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order_id: orderId }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
            setStatus("confirmed");
        } catch (err) {
            setError(err.message);
            setStatus("qr");
        }
    };

    const close = () => {
        setOpen(false);
        setStatus("idle");
        setForm(EMPTY);
        setOrderId("");
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[60] flex items-end justify-center bg-plum/60 backdrop-blur-sm sm:items-center sm:px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={close}
                    data-testid="checkout-modal"
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ y: 60, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8"
                        data-testid="checkout-panel"
                    >
                        <button
                            onClick={close}
                            aria-label="Close checkout"
                            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-blush text-burgundy transition-colors hover:bg-blushline"
                            data-testid="checkout-close-button"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        {status === "confirmed" ? (
                            <div className="py-2 text-center" data-testid="checkout-confirmed-message">
                                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blush">
                                    <Check className="h-6 w-6 text-gold" strokeWidth={3} />
                                </span>
                                <h3 className="mt-4 font-serif text-2xl leading-snug text-plum">
                                    Order confirmed, {form.name.split(" ")[0]}!
                                </h3>
                                <p
                                    className="mx-auto mt-3 inline-flex items-center gap-2 rounded-full bg-ivory px-4 py-1.5 font-sans text-[0.7rem] font-bold uppercase tracking-[0.16em] text-burgundy"
                                    data-testid="checkout-order-id-confirmed"
                                >
                                    Order ID: {orderId}
                                </p>
                                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-mutedtext">
                                    Your payment screenshot is received. Your guide PDF will be sent to you personally
                                    on WhatsApp and email, usually within a few hours.
                                </p>
                                <p className="mt-4 text-xs leading-relaxed text-mutedtext/80">
                                    Keep your Order ID handy. Questions? WhatsApp us anytime.
                                </p>
                                <motion.button
                                    onClick={close}
                                    whileTap={{ scale: 0.97 }}
                                    className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-burgundy px-8 py-4 font-sans text-sm font-bold uppercase tracking-[0.14em] text-cream shadow-xl shadow-burgundy/25 transition-colors duration-300 hover:bg-plum"
                                    data-testid="checkout-done-button"
                                >
                                    Done
                                </motion.button>
                            </div>
                        ) : status === "qr" || status === "claiming" ? (
                            <div className="py-2 text-center" data-testid="checkout-payment-step">
                                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blush">
                                    <Check className="h-6 w-6 text-gold" strokeWidth={3} />
                                </span>
                                <h3 className="mt-4 font-serif text-2xl leading-snug text-plum">
                                    Pay &#8377;49 to unlock your guide
                                </h3>
                                <p
                                    className="mx-auto mt-3 inline-flex items-center gap-2 rounded-full bg-ivory px-4 py-1.5 font-sans text-[0.7rem] font-bold uppercase tracking-[0.16em] text-burgundy"
                                    data-testid="checkout-order-id"
                                >
                                    Order ID: {orderId}
                                </p>
                                <div className="mt-5 flex flex-col items-center gap-3 rounded-2xl border border-blushline bg-ivory px-4 py-5">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/upi-qr-49-paytm.png`}
                                        alt="Paytm UPI QR code to pay ₹49 — 9560432288@ptsbi"
                                        className="h-44 w-44"
                                        data-testid="checkout-qr-image"
                                    />
                                    <p className="text-sm leading-relaxed text-mutedtext">
                                        <strong className="text-plum">Step 1 &mdash; Pay:</strong> scan with any UPI app &mdash; GPay,
                                        PhonePe, Paytm or BHIM &mdash; ₹49 pre-filled. Or pay by UPI ID:{" "}
                                        <strong className="text-burgundy">9560432288@ptsbi</strong>
                                    </p>
                                </div>
                                <motion.a
                                    href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(
                                        `New order \u2014 5-Day Husband Reset Guide (\u20b949)\nOrder ID: ${orderId}\nFull Name: ${form.name}\nDOB: ${form.dob}\nMobile: ${form.phone}\nEmail: ${form.email}\n\nI've paid \u20b949 via UPI \u2014 my payment screenshot is attached.`
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileTap={{ scale: 0.97 }}
                                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/50 bg-ivory px-6 py-3.5 font-sans text-[0.72rem] font-bold uppercase tracking-[0.14em] text-burgundy transition-colors duration-300 hover:bg-blush/60"
                                    data-testid="checkout-whatsapp-screenshot-button"
                                >
                                    Step 2 &mdash; Send Payment Screenshot on WhatsApp
                                </motion.a>
                                <motion.button
                                    onClick={claim}
                                    disabled={status === "claiming"}
                                    whileTap={{ scale: 0.97 }}
                                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-goldlight to-gold px-8 py-4 font-sans text-sm font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-gold/30 disabled:opacity-60"
                                    data-testid="checkout-paid-button"
                                >
                                    {status === "claiming" ? "Confirming your order\u2026" : "Step 3 \u2014 I've Sent It: Confirm My Order \u2192"}
                                </motion.button>
                                <p className="mt-3 text-xs leading-relaxed text-mutedtext">
                                    After you send the screenshot, your guide PDF is delivered personally to your
                                    WhatsApp and email.
                                </p>
                                {error && (
                                    <p className="mt-4 text-sm text-burgundy" data-testid="checkout-error-message">
                                        {error}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <p className="font-sans text-[0.62rem] font-bold uppercase tracking-[0.26em] text-gold">
                                    Almost there
                                </p>
                                <h3 className="mt-2 pr-10 font-serif text-2xl leading-snug text-plum">
                                    Get Your 5-Day Husband Reset Guide &mdash; &#8377;49
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-mutedtext">
                                    Fill in your details so we can send your purchase confirmation and guide link.
                                </p>
                                <form onSubmit={submit} className="mt-5 space-y-3" data-testid="checkout-form">
                                    <input
                                        type="text"
                                        required
                                        minLength={2}
                                        maxLength={80}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Full name"
                                        aria-label="Full name"
                                        className="w-full rounded-full border border-blushline bg-ivory px-5 py-3.5 text-sm text-ink placeholder:text-mutedtext/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="checkout-name-input"
                                    />
                                    <input
                                        type="date"
                                        required
                                        value={form.dob}
                                        onChange={set("dob")}
                                        aria-label="Date of birth"
                                        className="w-full rounded-full border border-blushline bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="checkout-dob-input"
                                    />
                                    <input
                                        type="tel"
                                        required
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="Mobile number (WhatsApp)"
                                        aria-label="Mobile number"
                                        className="w-full rounded-full border border-blushline bg-ivory px-5 py-3.5 text-sm text-ink placeholder:text-mutedtext/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="checkout-phone-input"
                                    />
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="Email ID"
                                        aria-label="Email ID"
                                        className="w-full rounded-full border border-blushline bg-ivory px-5 py-3.5 text-sm text-ink placeholder:text-mutedtext/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="checkout-email-input"
                                    />
                                    {status === "error" && (
                                        <p className="text-sm text-burgundy" data-testid="checkout-error-message">
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={status === "sending"}
                                        className="w-full rounded-full bg-burgundy px-8 py-4 font-sans text-sm font-bold uppercase tracking-[0.14em] text-cream shadow-xl shadow-burgundy/25 transition-colors duration-300 hover:bg-plum disabled:opacity-60"
                                        data-testid="checkout-submit-button"
                                    >
                                        {status === "sending" ? "Sending\u2026" : "Continue to Payment \u2192"}
                                    </button>
                                    <p className="text-center text-[0.7rem] leading-relaxed text-mutedtext/80">
                                        We use your details only to deliver the guide and confirmation. No spam.
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CheckoutModal;
